import AuthService from './AuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';

// Helper function to get headers with auth
const getHeaders = (includeContentType = true) => {
  const headers = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const authHeader = AuthService.getAuthHeader();
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }
  return headers;
};

/**
 * AdminService - Handles user administration, roles, and connected systems
 */
class AdminService {

  // ==================== User Management ====================

  /**
   * Create a new user (admin only)
   * @param {Object} userData - User data (email, password, firstName, lastName, username, warehouseCode)
   * @returns {Promise<Object>} Created user object
   */
  static async createUser(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  /**
   * Get all users in the system, optionally filtered by tenant (admin only)
   * @param {string} tenantId - Optional tenant ID to filter by
   * @returns {Promise<Array>} Array of user objects
   */
  static async getAllUsers(tenantId = null) {
    try {
      const params = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
      const response = await fetch(`${API_BASE_URL}/v1/admin/users${params}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  }

  // ==================== Tenant Management ====================

  /**
   * Get all tenants (admin only)
   * @param {boolean} includeInactive - Whether to include inactive tenants
   * @returns {Promise<Array>} Array of tenant objects
   */
  static async getAllTenants(includeInactive = true) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants?includeInactive=${includeInactive}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tenants:', error);
      throw error;
    }
  }

  /**
   * Get tenant by ID (admin only)
   * @param {string} tenantId - The tenant ID
   * @returns {Promise<Object>} Tenant object
   */
  static async getTenant(tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${encodeURIComponent(tenantId)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tenant:', error);
      throw error;
    }
  }

  /**
   * Get tenant details including user and warehouse counts (admin only)
   * @param {string} tenantId - The tenant ID
   * @returns {Promise<Object>} Tenant details
   */
  static async getTenantDetails(tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${encodeURIComponent(tenantId)}/details`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting tenant details:', error);
      throw error;
    }
  }

  /**
   * Create a new tenant (admin only)
   * @param {string} name - Tenant name
   * @returns {Promise<Object>} Created tenant
   */
  static async createTenant(name, tenantCode) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ name, tenantCode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating tenant:', error);
      throw error;
    }
  }

  /**
   * Update a tenant (admin only)
   * @param {string} tenantId - The tenant ID
   * @param {string} name - New tenant name
   * @returns {Promise<Object>} Updated tenant
   */
  static async updateTenant(tenantId, name, tenantCode, planTier) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${encodeURIComponent(tenantId)}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ name, tenantCode, planTier }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating tenant:', error);
      throw error;
    }
  }

  /**
   * Toggle tenant active status (admin only)
   * @param {string} tenantId - The tenant ID
   * @returns {Promise<Object>} Updated tenant
   */
  static async toggleTenantActive(tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${encodeURIComponent(tenantId)}/toggle-active`, {
        method: 'PUT',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error toggling tenant active:', error);
      throw error;
    }
  }

  /**
   * Delete a tenant (admin only)
   * @param {string} tenantId - The tenant ID
   * @returns {Promise<Object>} Deletion result
   */
  static async deleteTenant(tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${encodeURIComponent(tenantId)}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting tenant:', error);
      throw error;
    }
  }

  /**
   * Reset a tenant back to its first-login state (admin only) -- purges all
   * WMS and FAST data for the tenant, but keeps the tenant record and its
   * users intact so the same login still works. Distinct from deleteTenant,
   * which removes the tenant/users entirely.
   * @param {string} tenantId - The tenant ID
   * @returns {Promise<Object>} Reset result
   */
  static async resetTenant(tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${encodeURIComponent(tenantId)}/reset`, {
        method: 'POST',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error resetting tenant:', error);
      throw error;
    }
  }

  /**
   * Get warehouses for a specific tenant (admin only)
   * @param {string} tenantId - The tenant ID
   * @returns {Promise<Array>} Array of warehouse objects
   */
  static async getWarehousesByTenant(tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/tenants/${encodeURIComponent(tenantId)}/warehouses`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting warehouses by tenant:', error);
      throw error;
    }
  }

  /**
   * Get a specific user by ID (admin only)
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} User object
   */
  static async getUser(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  /**
   * Update a user (admin only)
   * @param {string} userId - The user ID
   * @param {Object} userData - Updated user data (firstName, lastName, username, etc.)
   * @returns {Promise<Object>} Updated user object
   */
  static async updateUser(userId, userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Delete a user and all associated data (admin only)
   * @param {string} userId - The user ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  static async deleteUser(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  /**
   * Toggle user active status (admin only)
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} Updated user object
   */
  static async toggleUserActive(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}/toggle-active`, {
        method: 'PUT',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error toggling user active status:', error);
      throw error;
    }
  }

  // ==================== External System Sync ====================

  /**
   * Get external system sync status (admin only)
   * @returns {Promise<Object>} Sync status with enabled, reachable, baseUrl
   */
  static async getExternalSyncStatus() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/provisioning/status`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting external sync status:', error);
      throw error;
    }
  }

  /**
   * Fetch users from external system (admin only)
   * @returns {Promise<Object>} Object with users array and count
   */
  static async fetchExternalUsers() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/wms/users`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching external users:', error);
      throw error;
    }
  }

  /**
   * Push a single user to external system (admin only)
   * @param {string} userId - The FAST user ID to push
   * @param {number} externalUserId - Optional external user ID to update
   * @returns {Promise<Object>} Push result
   */
  static async pushUserToExternalSystem(userId, externalUserId = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/provisioning/users/${encodeURIComponent(userId)}/push`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ externalUserId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error pushing user to external system:', error);
      throw error;
    }
  }

  /**
   * Fetch users filtered by connected system codes (admin only)
   * @param {Array<string>} systemCodes - Array of system codes to filter by
   * @returns {Promise<Object>} Object with users array filtered by systems
   */
  static async fetchUsersByConnectedSystems(systemCodes = []) {
    try {
      const params = systemCodes.length > 0
        ? `?${systemCodes.map(code => `systemCodes=${encodeURIComponent(code)}`).join('&')}`
        : '';
      const response = await fetch(`${API_BASE_URL}/v1/admin/wms/users/by-system${params}`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching users by systems:', error);
      throw error;
    }
  }

  /**
   * Retry provisioning for a failed user (admin only)
   * @param {string} userId - The FAST user ID to retry
   * @returns {Promise<Object>} Retry result with success status
   */
  static async retryProvisioning(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/provisioning/users/${encodeURIComponent(userId)}/retry`, {
        method: 'POST',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error retrying WMS provision:', error);
      throw error;
    }
  }

  /**
   * Send (or resend) the welcome email to a user
   * @param {string} userId
   * @returns {Promise<Object>}
   */
  static async sendWelcomeEmail(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}/send-welcome-email`, {
        method: 'POST',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending welcome email:', error);
      throw error;
    }
  }

  // ==================== Role Management ====================

  /**
   * Get available user roles (admin only)
   * @returns {Promise<Array>} Array of role objects with id, code, displayName, description
   */
  static async getAvailableRoles() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/roles`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting available roles:', error);
      throw error;
    }
  }

  /**
   * Get roles assigned to a user (admin only)
   * @param {string} userId - The user ID
   * @returns {Promise<Array>} Array of role objects assigned to the user
   */
  static async getUserRoles(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}/roles`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting user roles:', error);
      throw error;
    }
  }

  /**
   * Update user roles (admin only)
   * @param {string} userId - The user ID
   * @param {Array<string>} roleCodes - Array of role codes to assign
   * @returns {Promise<Object>} Update result
   */
  static async updateUserRoles(userId, roleCodes) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}/roles`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ roles: roleCodes }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user roles:', error);
      throw error;
    }
  }

  /**
   * Create a new role (admin only)
   * @param {Object} roleData - Role data (code, displayName, description, permissions)
   * @returns {Promise<Object>} Created role object
   */
  static async createRole(roleData) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/roles`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(roleData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }

  /**
   * Update a role (admin only)
   * @param {string} roleId - The role ID
   * @param {Object} roleData - Updated role data (displayName, description, permissions)
   * @returns {Promise<Object>} Updated role object
   */
  static async updateRole(roleId, roleData) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/roles/${encodeURIComponent(roleId)}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(roleData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  /**
   * Delete a role (admin only)
   * @param {string} roleId - The role ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  static async deleteRole(roleId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/roles/${encodeURIComponent(roleId)}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  }

  /**
   * Get users assigned to a specific role (admin only)
   * @param {string} roleCode - The role code
   * @returns {Promise<Array>} Array of users with this role
   */
  static async getUsersByRole(roleCode) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/roles/${encodeURIComponent(roleCode)}/users`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting users by role:', error);
      throw error;
    }
  }

  // ==================== Connected System Management ====================

  /**
   * Get available connected systems (admin only)
   * @returns {Promise<Array>} Array of connected system objects with id, systemCode, systemName
   */
  static async getConnectedSystems() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/connected-systems`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting connected systems:', error);
      throw error;
    }
  }

  /**
   * Update user's default connected system (admin only)
   * @param {string} userId - The user ID
   * @param {string} systemCode - The system code to assign
   * @returns {Promise<Object>} Update result
   */
  static async updateUserConnectedSystem(userId, systemCode) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}/connected-system`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ systemCode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user connected system:', error);
      throw error;
    }
  }

  // ==================== Warehouse Assignment ====================

  /**
   * Get warehouses assigned to a user (admin only)
   * @param {string} userId - The user ID
   * @returns {Promise<Array>} Array of warehouse objects with id, systemCode, systemName, isDefault
   */
  static async getUserWarehouses(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}/warehouses`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting user warehouses:', error);
      throw error;
    }
  }

  /**
   * Update user's warehouse assignments (admin only)
   * @param {string} userId - The user ID
   * @param {Array<string>} warehouseIds - Array of warehouse IDs to assign
   * @param {string} defaultWarehouseId - Optional ID of the default warehouse
   * @returns {Promise<Object>} Update result
   */
  static async updateUserWarehouses(userId, warehouseIds, defaultWarehouseId = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}/warehouses`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          warehouseIds,
          defaultWarehouseId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user warehouses:', error);
      throw error;
    }
  }

  /**
   * Get detailed user information including roles and connected systems (admin only)
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} User details with roles and connected systems
   */
  static async getUserDetails(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/admin/users/${encodeURIComponent(userId)}/details`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting user details:', error);
      throw error;
    }
  }

  // ==================== User Profile ====================

  /**
   * Update user profile (first name, last name, username, password)
   */
  static async updateUserProfile(profileData) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/users/profile`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
        error.response = { data: errorData };
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }
}

export default AdminService;
